import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import { useSessionContext } from '@/auth/useSessionContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { BusinessStatusScreen } from '@/sections/@dashboard/business-status';
import { isBusinessLocked } from '@/utils/businessLock';
import LoadingScreen from '@/components/loading-screen';
import {
  DASHBOARD_HOME_PATH,
  DASHBOARD_V2_HOME_PATH,
  getDashboardHomePath,
  hasDashboardV2,
} from '@/utils/dashboard';

import { DashboardHomePage, DashboardV2, OperationHealthPage } from '../../routes/elements';

/**
 * A Home escolhe entre o painel e a tela de status pelo negócio ativo, mas a
 * lista de negócios é consulta de fundo do layout: a porta de transição não
 * espera por ela. Até ela chegar, o painel fica montado e escondido atrás do
 * loading. Montado, porque as requisições dele começam junto com a dos negócios,
 * e não depois. Escondido, para um negócio travado não mostrar o painel antes da
 * tela de status.
 */
function WaitForActiveBusiness({ children }) {
  const { isLoading } = useBusiness();

  return (
    <>
      {isLoading && <LoadingScreen />}

      <Box sx={{ width: 1, ...(isLoading && { visibility: 'hidden' }) }}>{children}</Box>
    </>
  );
}

WaitForActiveBusiness.propTypes = {
  children: PropTypes.node,
};

export function DashboardHomeGate() {
  const { user, isInitialized } = useSessionContext();
  const { activeBusiness } = useBusiness();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (isBusinessLocked(activeBusiness)) {
    return <BusinessStatusScreen />;
  }

  if (hasDashboardV2(user)) {
    return <Navigate to={DASHBOARD_V2_HOME_PATH} replace />;
  }

  return (
    <WaitForActiveBusiness>
      <DashboardHomePage />
    </WaitForActiveBusiness>
  );
}

export function DashboardV2Gate() {
  const { user, isInitialized } = useSessionContext();
  const { activeBusiness } = useBusiness();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (isBusinessLocked(activeBusiness)) {
    return <BusinessStatusScreen />;
  }

  if (!hasDashboardV2(user)) {
    return <Navigate to={DASHBOARD_HOME_PATH} replace />;
  }

  return (
    <WaitForActiveBusiness>
      <DashboardV2 />
    </WaitForActiveBusiness>
  );
}

export function OperationHealthGate() {
  const { user, isInitialized } = useSessionContext();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!hasDashboardV2(user)) {
    return <Navigate to={getDashboardHomePath(user)} replace />;
  }

  return <OperationHealthPage />;
}

export function DashboardIndexRedirect() {
  const { user, isInitialized } = useSessionContext();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return <Navigate to={getDashboardHomePath(user)} replace />;
}
